import axios, { AxiosError, InternalAxiosRequestConfig } from 'axios';
import config from '../config';
import { getCookie, setCookie, eraseCookie } from '../utils/cookies';

// Types
export interface ApiError {
  error: string;
  message?: string;
  status?: number;
}

// Create axios instance
const api = axios.create({
  baseURL: config.api.baseUrl,
  headers: {
    'Content-Type': 'application/json',
  },
  timeout: config.api.timeout,
});

// Token management
export const tokenManager = {
  getAccessToken: (): string | null => getCookie(config.storage.accessTokenKey),
  getRefreshToken: (): string | null => getCookie(config.storage.refreshTokenKey),

  setTokens: (accessToken: string, refreshToken: string): void => {
    setCookie(config.storage.accessTokenKey, accessToken, 1); // 1 day
    setCookie(config.storage.refreshTokenKey, refreshToken, 30); // 30 days
  },

  clearTokens: (): void => {
    eraseCookie(config.storage.accessTokenKey);
    eraseCookie(config.storage.refreshTokenKey);
  },

  hasTokens: (): boolean => {
    return !!getCookie(config.storage.accessTokenKey);
  }
};

// Request interceptor - Add auth token
api.interceptors.request.use(
  (config: InternalAxiosRequestConfig) => {
    const token = tokenManager.getAccessToken();
    if (token && config.headers) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// Response interceptor - Handle errors and refresh token
let isRefreshing = false;
let failedQueue: Array<{
  resolve: (value?: unknown) => void;
  reject: (reason?: unknown) => void;
}> = [];

const processQueue = (error: Error | null, token: string | null = null) => {
  failedQueue.forEach((prom) => {
    if (error) {
      prom.reject(error);
    } else {
      prom.resolve(token);
    }
  });
  failedQueue = [];
};

api.interceptors.response.use(
  (response) => response,
  async (error: AxiosError<ApiError>) => {
    const originalRequest = error.config as InternalAxiosRequestConfig & { _retry?: boolean };

    // Handle 401 - Try to refresh token
    if (error.response?.status === 401 && !originalRequest._retry) {
      if (isRefreshing) {
        return new Promise((resolve, reject) => {
          failedQueue.push({ resolve, reject });
        })
          .then((token) => {
            if (originalRequest.headers) {
              originalRequest.headers.Authorization = `Bearer ${token}`;
            }
            return api(originalRequest);
          })
          .catch((err) => Promise.reject(err));
      }

      originalRequest._retry = true;
      isRefreshing = true;

      const refreshToken = tokenManager.getRefreshToken();

      if (!refreshToken) {
        tokenManager.clearTokens();
        window.location.href = config.app.baseUrl;
        return Promise.reject(error);
      }

      try {
        const response = await axios.post(`${config.api.baseUrl}/auth/refresh`, {
          refresh_token: refreshToken,
        });

        // Backend returns wrapped response: { success: true, data: { ... } }
        const { access_token, refresh_token: new_refresh_token } = response.data.data;

        tokenManager.setTokens(access_token, new_refresh_token);

        processQueue(null, access_token);

        if (originalRequest.headers) {
          originalRequest.headers.Authorization = `Bearer ${access_token}`;
        }
        return api(originalRequest);
      } catch (refreshError) {
        processQueue(refreshError as Error, null);
        tokenManager.clearTokens();
        window.location.href = config.app.baseUrl;
        return Promise.reject(refreshError);
      } finally {
        isRefreshing = false;
      }
    }

    return Promise.reject(error);
  }
);

export default api;
